import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Anchor, Award, Clock, Shield } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { services, destinations, media, contactInfo } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, SectionHeader } from "@/components/ui-primitives";
import AnimatedCounter from "@/components/AnimatedCounter";
import AnimatedServiceIcon from "@/components/AnimatedServiceIcon";
import PartnersStrip from "@/components/PartnersStrip";
import TestimonialsSection from "@/components/TestimonialsSection";

// Cinematic letter-by-letter reveal for hero headline
const RevealText = ({ text, className = "", delay = 0, italic = false }) => {
  const words = text.split(" ");
  return (
    <motion.span
      className={`inline-block ${italic ? "italic" : ""} ${className}`}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: 0.04, delayChildren: delay },
        },
      }}
    >
      {words.map((word, wi) => (
        <span
          key={wi}
          className="inline-block whitespace-nowrap"
          style={{ marginRight: "0.25em" }}
        >
          {Array.from(word).map((char, i) => (
            <motion.span
              key={i}
              className="inline-block"
              variants={{
                hidden: { y: "110%", opacity: 0 },
                visible: {
                  y: 0,
                  opacity: 1,
                  transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              {char}
            </motion.span>
          ))}
        </span>
      ))}
    </motion.span>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section
      className="relative min-h-screen flex items-end overflow-hidden"
      data-testid="home-hero"
    >
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={media.heroMain}
          className="w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source
            src="/assets/videos/dji_fly_20250716_110038_499_1752653041599_quickshot.mp4"
            type="video/mp4"
          />
          {/* Fallback image shown if video cannot load */}
          <img
            src={media.heroMain}
            alt="Luxury superyacht at Turkish marina at golden hour"
            className="w-full h-full object-cover"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-transparent to-transparent" />
      </div>

      <div className="relative px-6 sm:px-10 lg:px-16 pb-24 pt-40 w-full">
        <div className="max-w-5xl">
          <FadeUp>
            <div className="flex items-center gap-4 mb-8">
              <div className="gold-rule" />
              <span className="overline">{t.hero.eyebrow}</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-white leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl" style={{ wordBreak: "keep-all" }}>
              <span className="block overflow-hidden pb-[0.15em]">
                <RevealText text={t.hero.title} className="text-brand" italic delay={0.3} />
              </span>
              <span className="block overflow-hidden mt-1 pb-[0.15em]">
                <RevealText text={t.hero.titleLine2} className="text-white" delay={0.6} />
              </span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed font-light">
              {t.hero.subtitle}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-brand text-white px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
                data-testid="hero-cta-request"
              >
                {t.hero.ctaPrimary} <ArrowRight size={14} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:border-brand hover:bg-brand hover:text-white transition-colors"
                data-testid="hero-cta-services"
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </FadeUp>
        </div>
      </div>

      <div className="absolute bottom-10 right-10 hidden lg:flex items-center gap-3 text-white/60 text-[10px] tracking-[0.3em] uppercase rotate-[-90deg] origin-bottom-right">
        <span>Scroll</span>
        <div className="w-10 h-px bg-white/40" />
      </div>
    </section>
  );
};

const EtaMicroForm = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [yacht, setYacht] = React.useState("");
  const [eta, setEta] = React.useState("");
  const [port, setPort] = React.useState("");

  const t = {
    en: { eyebrow: "Quick ETA · Pre-Notify", title: "Heading to Türkiye?", sub: "Send your yacht's ETA — we'll line up berth, clearance and provisioning before you drop anchor.", yacht: "Yacht name", date: "Estimated arrival date", port: "Arrival port (e.g. Sinop, İstanbul, Yalıkavak)", cta: "Request Pre-Arrival Brief" },
    ru: { eyebrow: "Быстрый ETA · Предуведомление", title: "Идёте в Турцию?", sub: "Сообщите ETA яхты — мы подготовим стоянку, оформление и снабжение до постановки на якорь.", yacht: "Название яхты", date: "Ожидаемая дата прибытия", port: "Порт прибытия (например, Синоп, Стамбул, Ялыкавак)", cta: "Запросить предварительный бриф" },
    tr: { eyebrow: "Hızlı ETA · Ön Bildirim", title: "Türkiye'ye mi geliyorsunuz?", sub: "Yatınızın ETA'sını gönderin — siz demir atmadan yer, çıkış işlemleri ve ikmali hazırlayalım.", yacht: "Yat adı", date: "Tahmini varış tarihi", port: "Varış limanı (örn. Sinop, İstanbul, Yalıkavak)", cta: "Geliş Öncesi Brifing Talep Et" },
  }[lang] || t.en;

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `ETA: ${eta || "TBC"}\nYacht: ${yacht || "TBC"}\nArrival port: ${port || "TBC"}`;
    navigate("/contact", { state: { etaPrefill: { yacht, eta, port, message, location: port } } });
  };

  return (
    <section className="relative border-b border-navy/10 bg-cream px-6 sm:px-10 lg:px-16 py-12" data-testid="eta-microform">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end max-w-7xl mx-auto">
        <div className="lg:col-span-4">
          <div className="overline mb-3" style={{ color: "#C9A24A" }}>{t.eyebrow}</div>
          <h2 className="font-display text-3xl sm:text-4xl text-navy leading-tight">{t.title}</h2>
          <p className="mt-3 text-navy/65 text-sm leading-relaxed">{t.sub}</p>
        </div>
        <form onSubmit={handleSubmit} className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-12 gap-3">
          <input
            type="text"
            value={yacht}
            onChange={(e) => setYacht(e.target.value)}
            placeholder={t.yacht}
            className="sm:col-span-3 bg-white border border-navy/15 px-4 py-3.5 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-brand"
            data-testid="eta-yacht"
          />
          <input
            type="date"
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            placeholder={t.date}
            className="sm:col-span-3 bg-white border border-navy/15 px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-brand"
            data-testid="eta-date"
          />
          <input
            type="text"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            placeholder={t.port}
            className="sm:col-span-3 bg-white border border-navy/15 px-4 py-3.5 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-brand"
            data-testid="eta-port"
          />
          <button
            type="submit"
            className="sm:col-span-3 bg-brand text-white px-5 py-3.5 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors flex items-center justify-center gap-2"
            data-testid="eta-submit"
          >
            {t.cta} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
};

const Stats = () => {
  const { t } = useLanguage();
  const items = [
    { value: 450, suffix: "K+", label: t.stats.operations, animated: true },
    { value: 35, suffix: "+", label: t.stats.years, animated: true },
    { value: 30, suffix: "+", label: t.stats.ports, animated: true },
    { value: "24/7", label: t.stats.support, animated: false },
  ];
  return (
    <section className="relative border-y border-navy/10 bg-cream">
      <div className="px-6 sm:px-10 lg:px-16 py-16 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
        {items.map((item, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div data-testid={`stat-${i}`}>
              <div className="font-display text-5xl sm:text-6xl text-brand leading-none">
                {item.animated ? (
                  <AnimatedCounter
                    value={item.value}
                    suffix={item.suffix || ""}
                    duration={1900}
                  />
                ) : (
                  item.value
                )}
              </div>
              <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-navy/60">
                {item.label}
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const ServicesGrid = () => {
  const { t, lang } = useLanguage();
  return (
    <section className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16" data-testid="home-services">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <div className="overline mb-5">{t.nav.services} · 01 — 09</div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05]">
              {t.home.servicesTitle}
            </h2>
          </FadeUp>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <FadeUp delay={0.15}>
            <p className="text-navy/70 text-base leading-relaxed">
              {t.home.servicesSubtitle}
            </p>
            <Link
              to="/services"
              className="mt-6 inline-flex items-center gap-2 text-brand text-[11px] tracking-[0.25em] uppercase hover:gap-3 transition-all"
              data-testid="home-services-viewall"
            >
              {t.common.allServices} <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10">
        {services.map((s, i) => {
          return (
            <FadeUp key={s.slug} delay={i * 0.04}>
              <Link
                to={`/services/${s.slug}`}
                className="group block bg-bone hover:bg-cream transition-colors p-8 h-full"
                data-testid={`home-service-card-${s.slug}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <AnimatedServiceIcon slug={s.slug} iconName={s.icon} size={28} />
                  <span className="font-display text-navy/20 text-xl">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl text-navy leading-tight mb-3 group-hover:text-brand transition-colors">
                  {s[lang].name}
                </h3>
                <p className="text-sm text-navy/70 leading-relaxed">{s[lang].short}</p>
                <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-brand/80 group-hover:gap-3 transition-all">
                  {t.common.viewService} <ArrowRight size={12} />
                </div>
              </Link>
            </FadeUp>
          );
        })}
      </div>
    </section>
  );
};

const WhyChooseUs = () => {
  const { t } = useLanguage();
  const icons = [Award, Anchor, Clock, Shield];
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-white" data-testid="home-why">
      <div className="absolute inset-0 pointer-events-none">
        <img src={media.texture} alt="" className="w-full h-full object-cover opacity-[0.08]" />
      </div>
      <div className="relative px-6 sm:px-10 lg:px-16">
        <SectionHeader
          eyebrow="Advantage"
          title={t.home.whyTitle}
          subtitle={t.home.whySubtitle}
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-navy/10">
          {t.home.whyPoints.map((p, i) => {
            const Icon = icons[i];
            return (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="bg-white p-10 h-full">
                  <Icon size={22} strokeWidth={1.25} className="text-brand mb-6" />
                  <h3 className="font-display text-2xl text-navy mb-3">{p.title}</h3>
                  <p className="text-sm text-navy/70 leading-relaxed">{p.body}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Coverage = () => null; // removed per request

const TrustStrip = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-cream border-y border-navy/10" data-testid="home-trust">
      <div className="px-6 sm:px-10 lg:px-16">
        <SectionHeader
          align="center"
          eyebrow="Partners"
          title={t.home.trustTitle}
          subtitle={t.home.trustSubtitle}
        />
        <div className="mt-14 overflow-hidden">
          <div className="marquee-track opacity-70">
            {[
              "Yalıkavak Marina",
              "D-Marin",
              "Ece Saray",
              "Netsel Marmaris",
              "Port Göcek",
              "Çelebi Marina",
              "Atakoy Marina",
              "Milta Bodrum",
            ]
              .concat([
                "Yalıkavak Marina",
                "D-Marin",
                "Ece Saray",
                "Netsel Marmaris",
                "Port Göcek",
                "Çelebi Marina",
                "Atakoy Marina",
                "Milta Bodrum",
              ])
              .map((name, idx) => (
                <div
                  key={idx}
                  className="flex items-center mx-10 text-navy/60 font-display text-2xl italic whitespace-nowrap"
                >
                  {name}
                  <span className="ml-10 w-1 h-1 bg-brand rounded-full" />
                </div>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const QuickContactStrip = () => {
  const { t } = useLanguage();
  return (
    <section className="relative py-24 overflow-hidden" data-testid="home-quick-contact">
      <div className="absolute inset-0">
        <img src={media.crewOps} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-bone/80" />
      </div>
      <div className="relative px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-10">
          <div className="md:col-span-7">
            <FadeUp>
              <div className="overline mb-5">ETA support</div>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                {t.home.quickContactTitle}
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 text-navy/70 text-lg leading-relaxed max-w-xl">
                {t.home.quickContactBody}
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <FadeUp delay={0.15}>
              <div className="glass p-8">
                <div className="overline mb-4">{t.nav.contact}</div>
                <div className="space-y-3 text-sm">
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="block text-navy hover:text-brand transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                  <a
                    href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                    className="block text-navy hover:text-brand transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-brand hover:text-brand-soft"
                  >
                    WhatsApp · {contactInfo.whatsappDisplay}
                  </a>
                </div>
                <Link
                  to="/contact"
                  className="mt-6 block text-center bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
                  data-testid="quickcontact-cta"
                >
                  {t.common.requestService}
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { lang } = useLanguage();
  return (
    <>
      <SEO
        path="/"
        title={
          lang === "ru"
            ? "Яхтенное агентирование в Турции"
            : "Yacht Agency Services in Türkiye"
        }
        description={
          lang === "ru"
            ? "Rota Yachting — полноценное яхтенное агентство в Турции. Портовое оформление, стоянки, таможня, снабжение, консьерж. 24/7."
            : "Rota Yachting — full-service yacht agency in Türkiye. Port clearance, berth reservation, customs, provisioning, concierge. 24/7 operations."
        }
        keywords="yacht agency Turkey, yacht services Turkey, yacht clearance Turkey, superyacht agency Turkey, yacht support Türkiye"
      />
      <Hero />
      <EtaMicroForm />
      <Stats />
      <ServicesGrid />
      <WhyChooseUs />
      <TestimonialsSection />
      <PartnersStrip />
      <QuickContactStrip />
    </>
  );
};

export default Home;
